import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ArtistCardProps {
  artist: {
    id: string;
    name: string;
    category: string;
    genre: string;
    bio: string;
    imageUrl: string;
    avatarUrl: string;
    featured: boolean;
    eventTypes: string[];
    phone: string;
    email: string;
    website: string;
    social: {
      instagram: string;
      facebook: string;
      youtube: string;
    };
  };
}

export default function ArtistCard({ artist }: ArtistCardProps) {
  const { t } = useTranslation('artists');

  return (
    <div className="group bg-white rounded-2xl md:rounded-3xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all duration-300">
      {/* Image */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={artist.imageUrl}
          alt={artist.name}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
        {artist.featured && (
          <div className="absolute top-3 left-3">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-amber-600 text-white rounded-full">
              <i className="ri-star-fill text-xs"></i>
              Featured
            </span>
          </div>
        )}
        <div className="absolute top-3 right-3">
          <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-white/90 backdrop-blur-sm text-gray-700 rounded-full">
            {artist.category}
          </span>
        </div>
        <div className="absolute bottom-3 left-3 right-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full overflow-hidden border-2 border-white/80">
              <img
                src={artist.avatarUrl}
                alt={artist.name}
                className="w-full h-full object-cover"
              />
            </div>
            <div>
              <h3 className="text-sm font-bold text-white">{artist.name}</h3>
              <p className="text-xs text-white/70">{artist.genre}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 md:p-5">
        <p className="text-xs text-gray-500 leading-relaxed line-clamp-2 mb-3">
          {artist.bio}
        </p>

        {/* Event Tags */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {artist.eventTypes.slice(0, 3).map((et) => (
            <span key={et} className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full">
              {et}
            </span>
          ))}
          {artist.eventTypes.length > 3 && (
            <span className="px-2 py-0.5 text-xs bg-gray-50 text-gray-400 rounded-full">
              +{artist.eventTypes.length - 3}
            </span>
          )}
        </div>

        {/* Contact Actions */}
        <div className="flex items-center gap-2">
          <Link
            to={`/artista/${artist.id}`}
            className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium bg-gray-900 text-white rounded-full hover:bg-amber-700 transition-colors whitespace-nowrap"
          >
            {t('card.viewProfile')}
            <i className="ri-arrow-right-line text-xs"></i>
          </Link>
          <a
            href={`tel:${artist.phone}`}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
            title={artist.phone}
          >
            <i className="ri-phone-line text-sm"></i>
          </a>
          <a
            href={`mailto:${artist.email}`}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors"
            title={artist.email}
          >
            <i className="ri-mail-line text-sm"></i>
          </a>
        </div>
      </div>
    </div>
  );
}